import React from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link as ReactLink } from "react-router-dom";

import {
  ReturnFlight,
  AltArrowExpand,
  AltArrowBack,
  Info,
  AltArrowCollapse,
  Lounge,
  AirplaneRight,
} from "@airhelp/icons";
import { InfoBox } from "@airhelp/react";
import {
  Box,
  Button,
  Flex,
  Link,
  Text,
  VStack,
  Wrap,
  WrapItem,
  Accordion,
  useDisclosure,
  useBreakpointValue,
  Heading,
  Tag,
  TagLabel,
} from "@chakra-ui/react";

import AirBundleModal from "components/Journey/AirBundle/AirBundleModal";
import ProtectBox from "components/Journey/AirBundle/ProtectBox";
import JourneyActions from "components/Journey/JourneyActions";
import config from "config/config";
import { PAGE_PATHS } from "config/routes";
import useFetchAirBundleSubscription from "hooks/api/airBundle/useFetchAirBundleSubscription/useFetchAirBundleSubscription";
import { Journey } from "models/Journey";
import { journeyUtils } from "utils";
import chakraStyles from "utils/styles/chakra";

import AirportName from "./AirportName";
import FlightCard from "./FlightCard";

const { flightStatuses } = config;

interface IComponent {
  journey: Journey;
  showAirPayoutSubscriptionModal: boolean;
  userDidNotSelectAirLuggage: boolean;
}

const TripCard: React.FC<IComponent> = ({ journey, showAirPayoutSubscriptionModal, userDidNotSelectAirLuggage }) => {
  const { t, i18n } = useTranslation();
  const { data: airBundleSubscriptionData } = useFetchAirBundleSubscription({
    enabled: showAirPayoutSubscriptionModal,
    gcTime: 0,
  });

  const itineraries = journey.itineraries;
  const journeyName = journey.generateDisplayName(t, i18n.language);

  const hasAirPayout = journey.hasAirPayout;
  const hasAirLuggage = journey.hasAirLuggage;

  const hasItineraries = itineraries && itineraries.length > 0;

  const showAirBundleSubscriptionModal = airBundleSubscriptionData && showAirPayoutSubscriptionModal;

  const isDesktop = useBreakpointValue({ base: false, lg: true });

  const outboundOnly = journey.mainAirPayoutJourney?.outboundOnly;

  return (
    <>
      {showAirBundleSubscriptionModal && (
        <AirBundleModal
          journey={journey}
          airBundleSubscriptionData={airBundleSubscriptionData}
          userDidNotSelectAirLuggage={userDidNotSelectAirLuggage}
        />
      )}
      <Flex mt={4} mb={6} alignItems="center" justifyContent="space-between">
        <Flex alignItems="center">
          {isDesktop && (
            <Link
              as={ReactLink}
              to={generatePath(PAGE_PATHS.JOURNEYS_PAGE)}
              _hover={{ textUnderline: "none" }}
              _visited={{}}
            >
              <AltArrowBack width={5} height={5} mr={2} color="primary.500" _hover={{ color: "primary.600" }} />
            </Link>
          )}
          <Heading variant="h4" data-testid="journey-name">
            {journeyName}
          </Heading>
        </Flex>
        {isDesktop && <JourneyActions journey={journey} />}
      </Flex>
      <Flex justifyContent="flex-start" alignItems="flex-start" flexDirection={{ base: "column", md: "row" }}>
        <Box width="100%">
          {itineraries.map((itinerary, itineraryIndex) => {
            const { isOpen, onToggle } = useDisclosure();

            const isReturnTrip = itineraryIndex === 1 && itineraries.length == 2;
            const flights = itinerary.flights;

            const firstFlight = flights[0];
            const lastFlightIndex = flights.length - 1;
            const lastFlight = flights[lastFlightIndex];

            const departureAirport = firstFlight.departureAirport;
            const arrivalAirport = lastFlight.arrivalAirport;

            const pastFlight = new Date(firstFlight.localDepartureDate) < new Date();

            const claimNowUrl = journeyUtils.createClaimUrl(itinerary);

            const disruptedFlight = flights.find(
              (flight) =>
                (!pastFlight && flight.flightStatus?.departureStatus === flightStatuses.canceled) ||
                (!flight.flightStatus?.gmtActualDepartureAt &&
                  flight.flightStatus?.departureStatus === flightStatuses.delayed)
            );

            const hasDisruptedFlight = !!disruptedFlight;

            const getShowProtectedTag = () => {
              return !(outboundOnly && itineraryIndex !== 0);
            };

            return (
              <Box key={`itinerary-${itinerary.id}`} mb={4}>
                {isReturnTrip && (
                  <Flex fontSize="lg" fontWeight="medium" color="primary.900" alignItems="center" mb={4}>
                    <ReturnFlight width={5} height={5} mr={2} color="primary.500" />
                    {t("common.return")}
                  </Flex>
                )}
                {/* itinerary rounded box */}
                <Box borderRadius="xl" backgroundColor="greyscale.100" mb={4} pt={6} pb={2} px={{ base: 4, md: 5 }}>
                  {/* itinerary header */}
                  <VStack spacing={6} alignItems="flex-start">
                    {/* itinerary destination */}
                    <Box width="100%">
                      <Flex
                        alignItems="center"
                        justifyContent={{ base: "space-between", md: "flex-start" }}
                        color="primary.900"
                      >
                        <AirportName
                          airportCity={journeyUtils.cityTranslations(departureAirport)}
                          airportCode={departureAirport?.iata}
                          side="left"
                        />
                        <AirplaneRight color="greyscale.600" w={{ base: 6, md: 5 }} h="auto" mx={{ base: 4, md: 8 }} />
                        <AirportName
                          airportCity={journeyUtils.cityTranslations(arrivalAirport)}
                          airportCode={arrivalAirport?.iata}
                          side="right"
                        />
                      </Flex>
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        mt={3}
                        display={{ base: "flex", md: "none" }}
                      >
                        <Flex>
                          <AirplaneRight color="greyscale.600" w={5} h="auto" />
                          <Flex
                            fontWeight="bold"
                            fontSize="x-small"
                            color="greyscale.600"
                            borderRadius="full"
                            flex="none"
                            minWidth={5}
                            h={5}
                            alignItems="center"
                            justifyContent="center"
                          >
                            {flights.length}
                          </Flex>
                        </Flex>

                        {getShowProtectedTag() && (
                          <Tag size="xs" backgroundColor="success.200" color="success.500">
                            <TagLabel fontWeight="extrabold" textTransform="uppercase" data-testid="protected">
                              {t("common.protected")}
                            </TagLabel>
                          </Tag>
                        )}
                      </Flex>
                    </Box>

                    {/* itinerary actions */}
                    <Wrap
                      direction={{ base: "column", md: "row" }}
                      spacing={{ base: 4, md: 2 }}
                      justifyContent={{ base: "center", md: "flex-start" }}
                      flexWrap="wrap"
                      width="100%"
                      display={journey.loungeActive || itinerary.claimAllowed ? "block" : "none"}
                    >
                      {journey.loungeActive ? (
                        <WrapItem>
                          <Button
                            as={ReactLink}
                            to={{ pathname: generatePath(PAGE_PATHS.LOUNGES_PAGE) }}
                            variant="secondary"
                            size={{ base: "s", md: "xs" }}
                            width={{ base: "100%", md: "auto" }}
                            data-testid="itinerary-lounge-access"
                            textTransform="capitalize"
                          >
                            <Lounge mr={1} />
                            {t("common.lounge_access")}
                          </Button>
                        </WrapItem>
                      ) : null}
                      {itinerary.claimAllowed ? (
                        <WrapItem>
                          <Button
                            as={Link}
                            href={claimNowUrl}
                            size={{ base: "s", md: "xs" }}
                            width={{ base: "100%", md: "auto" }}
                            data-testid="itinerary-start-claim"
                            {...chakraStyles.buttonLinkStyles}
                          >
                            {t("flights_list.start_claim")}
                          </Button>
                        </WrapItem>
                      ) : null}
                    </Wrap>
                  </VStack>

                  {/* flights list */}
                  <Box mt={6}>
                    <Accordion
                      variant="link"
                      onClick={onToggle}
                      size="s"
                      mb="2"
                      width={{ base: "100%", md: "auto" }}
                      justifyContent="space-between"
                      fontWeight="500"
                      color="primary.900"
                      _focus={{ boxShadow: "none" }}
                      display={{ base: "flex", md: "none" }}
                      data-testid="show-more-journey-details"
                    >
                      <Flex alignItems="center">
                        <Box ml={2}>{t("flights_list.more_details")}</Box>
                        {hasDisruptedFlight && (
                          <Box ml={2}>
                            <Text
                              backgroundColor="danger.500"
                              borderRadius="full"
                              color="greyscale.100"
                              whiteSpace="nowrap"
                              fontSize="x-small"
                              fontWeight="semibold"
                              lineHeight="base"
                              textTransform={{ base: "uppercase", md: "initial" }}
                              px={2}
                              py={1}
                              letterSpacing="0.5px"
                              data-testid="disrupted-badge"
                            >
                              {t("common.disrupted")}
                            </Text>
                          </Box>
                        )}
                      </Flex>
                      {isOpen ? (
                        <AltArrowCollapse width={5} height={5} mr={3} color="primary.500" />
                      ) : (
                        <AltArrowExpand width={5} height={5} mr={3} color="primary.500" />
                      )}
                    </Accordion>
                    <Box display={{ base: isOpen ? "block" : "none", md: "block" }}>
                      {flights.map((flight, index) => {
                        const isConnectingFlight = index != lastFlightIndex;
                        return (
                          <FlightCard
                            flight={flight}
                            isConnectingFlight={isConnectingFlight}
                            getShowProtectedTag={getShowProtectedTag}
                            key={`flight-${flight.id}`}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
          {hasItineraries && (
            <InfoBox
              isChat
              backgroundColor="greyscale.100"
              icon={<Info color="primary.500" display="inline-flex" mb={1} />}
              variant="secondary"
              mb={6}
              border="none"
            >
              {t("flights_list.showing_local_times")}
            </InfoBox>
          )}
        </Box>
        {(hasAirPayout || hasAirLuggage) && (
          <Box width="100%" maxWidth={{ base: "100% ", md: "343px" }} ml={{ base: 0, md: 8 }}>
            <ProtectBox journey={journey} />
          </Box>
        )}
      </Flex>
    </>
  );
};

export default TripCard;
